import colors from '@/enum/colors'
import { multiQueueColumnsPropNames } from '@/helpers/multiQueueDashboardRedesign'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import { QueueDashboardConfig } from '@/types/dashboard'

export const ADDITIONAL_DATA_KEY = 'ExitCounts'
export const PERCENTAGE_TYPE = 'percentage'
export const COUNTERS_TYPE = 'counters'
export const TIME_TYPE = 'time'
export const TOTAL_CALLS_KEY = multiQueueColumnsPropNames.CALL_COUNT

export const OTHER_STATISTIC_LABEL = '% of Other'
export const QUEUE_RULE_NO_RULE = 'NO_RULE'
export const ALL_QUEUE_ID = 'All'

export const AVG_RING_TIME_KEY = multiQueueColumnsPropNames.AVG_RING_TIME

export const MAX_RING_TIME_KEY = multiQueueColumnsPropNames.MAX_RING_TIME

export const BASE_ACTIVITY_FONT = 24

export type MetricsState = Record<string,
    {
        count: number;
        value: number | string;
        label: string;
        type: string;
        color?: string,
        key: string
    }>

// Base config without labels - labels are added dynamically via getTranslatedLabel()
const PERCENTAGE_METRICS_BASE_CONFIG = {
    [multiQueueColumnsPropNames.ANSWER]: {
        key: multiQueueColumnsPropNames.ANSWER,
        color: colors.QUEUE_LIGHT_GREEN,
        order: 0,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.ABANDONED]: {
        key: multiQueueColumnsPropNames.ABANDONED,
        color: colors.QUEUE_PRIVATE_COLOR,
        order: 1,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.IVR_EXIT]: {
        key: multiQueueColumnsPropNames.IVR_EXIT,
        color: colors.QUEUE_PRIVATE_COLOR,
        order: 2,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.PICK_UP]: {
        key: multiQueueColumnsPropNames.PICK_UP,
        color: colors.QUEUE_LOGIN_COLOR,
        order: 3,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.TIMEOUT_EXIT]: {
        key: multiQueueColumnsPropNames.TIMEOUT_EXIT,
        color: colors.QUEUE_ADMINISTRATIVE_COLOR,
        order: 4,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.JOIN_EMPTY]: {
        key: multiQueueColumnsPropNames.JOIN_EMPTY,
        color: colors.QUEUE_LUNCH_COLOR,
        order: 5,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.LEAVE_EMPTY]: {
        key: multiQueueColumnsPropNames.LEAVE_EMPTY,
        color: colors.QUEUE_PRIVATE_COLOR,
        order: 6,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.JOIN_UNAVAIL]: {
        key: multiQueueColumnsPropNames.JOIN_UNAVAIL,
        color: colors.QUEUE_LOGOUT_COLOR,
        order: 7,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.LEAVE_UNAVAIL]: {
        key: multiQueueColumnsPropNames.LEAVE_UNAVAIL,
        color: colors.QUEUE_PRIVATE_COLOR,
        order: 8,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.FULL]: {
        key: multiQueueColumnsPropNames.FULL,
        color: colors.QUEUE_OTHER_COLOR,
        order: 9,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.NEXT_DESTINATION]: {
        key: multiQueueColumnsPropNames.NEXT_DESTINATION,
        color: colors.QUEUE_LOGOUT_COLOR,
        order: 10,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.NOT_IN_SLA_COUNT]: {
        key: multiQueueColumnsPropNames.NOT_IN_SLA_COUNT,
        color: colors.QUEUE_LOGOUT_COLOR,
        order: 11,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.IN_SLA_COUNT]: {
        key: multiQueueColumnsPropNames.IN_SLA_COUNT,
        color: colors.QUEUE_LUNCH_COLOR,
        order: 12,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.RECURRING_COUNT]: {
        key: multiQueueColumnsPropNames.RECURRING_COUNT,
        color: colors.QUEUE_LOGOUT_COLOR,
        order: 13,
        type: PERCENTAGE_TYPE,
    },

    [multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT]: {
        key: multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT,
        color: colors.QUEUE_LOGIN_COLOR,
        order: 17,
        type: PERCENTAGE_TYPE,
    }
}

const COUNTER_METRICS_BASE_CONFIG = {
    [multiQueueColumnsPropNames.CALL_COUNT]: {
        key: multiQueueColumnsPropNames.CALL_COUNT,
        order: 14,
        type: COUNTERS_TYPE,
        color: colors.QUEUE_LIGHT_GREEN
    },

    [multiQueueColumnsPropNames.TOTAL_ANSWERED_CALLBACKS]: {
        key: multiQueueColumnsPropNames.TOTAL_ANSWERED_CALLBACKS,
        order: 18,
        type: COUNTERS_TYPE,
        color: colors.QUEUE_LOGIN_COLOR
    },

    [multiQueueColumnsPropNames.AVG_ANSWER_HOURLY]: {
        key: multiQueueColumnsPropNames.AVG_ANSWER_HOURLY,
        order: 19,
        type: COUNTERS_TYPE,
        color: colors.QUEUE_LIGHT_GREEN
    },

    [multiQueueColumnsPropNames.AVG_CALLS_HOURLY]: {
        key: multiQueueColumnsPropNames.AVG_CALLS_HOURLY,
        order: 20,
        type: COUNTERS_TYPE,
        color: colors.QUEUE_LOGIN_COLOR
    }
}

const TIME_METRICS_BASE_CONFIG = {
    [multiQueueColumnsPropNames.MAX_RING_TIME]: {
        key: multiQueueColumnsPropNames.MAX_RING_TIME,
        order: 15,
        type: TIME_TYPE,
        color: colors.QUEUE_PRIVATE_COLOR
    },

    [multiQueueColumnsPropNames.AVG_RING_TIME]: {
        key: multiQueueColumnsPropNames.AVG_RING_TIME,
        order: 16,
        type: TIME_TYPE,
        color: colors.QUEUE_ADMINISTRATIVE_COLOR
    }
}

// Base metric config type (without label)
interface BaseMetricConfig {
    key: string
    color: string
    order: number
    type: string
}

// Metric config with translated label
interface MetricConfigWithLabel extends BaseMetricConfig {
    label: string
}

// Helper function to get translated label at runtime (when translations are loaded)
export function getTranslatedLabel (prop: string): string {
    const { makeTableHeaderTranslationsByProp } = useTableHeaderTranslations()
    return makeTableHeaderTranslationsByProp(prop).fullTextLabel
}

// Generate configs with translated labels - called at runtime when translations are available
function addLabelsToConfig (config: Record<string, BaseMetricConfig>): Record<string, MetricConfigWithLabel> {
    const result: Record<string, MetricConfigWithLabel> = {}
    for (const key in config) {
        result[key] = {
            ...config[key],
            label: getTranslatedLabel(config[key].key)
        }
    }
    return result
}

// This function returns metrics with dynamically translated labels
// Call this at runtime when you need translated labels
export function getAllMetrics () {
    const percentageWithLabels = addLabelsToConfig(PERCENTAGE_METRICS_BASE_CONFIG)
    const counterWithLabels = addLabelsToConfig(COUNTER_METRICS_BASE_CONFIG)
    const timeWithLabels = addLabelsToConfig(TIME_METRICS_BASE_CONFIG)

    return {
        ...percentageWithLabels,
        ...counterWithLabels,
        ...timeWithLabels
    }
}

// Static config without labels - use getAllMetrics() for translated labels
export const ALL_METRICS = {
    ...PERCENTAGE_METRICS_BASE_CONFIG,
    ...COUNTER_METRICS_BASE_CONFIG,
    ...TIME_METRICS_BASE_CONFIG
}

// Returns array with translated labels - call at runtime when translations are available
export function getAllQueueStatisticArray () {
    return Object.values(getAllMetrics())
}

export const activitiesToDisplayEnum = {
    answerCount: 'AnswerCount',
    InSLACount: 'InSLACount',
}

export const activitiesToDisplay = [
    {
        key: activitiesToDisplayEnum.answerCount,
        value: activitiesToDisplayEnum.answerCount,
        label: activitiesToDisplayEnum.answerCount
    },
    {
        key: activitiesToDisplayEnum.InSLACount,
        value: activitiesToDisplayEnum.InSLACount,
        label: activitiesToDisplayEnum.InSLACount
    }
]

export function buildDefaultQueueDashboardConfig (): QueueDashboardConfig {
    const config: QueueDashboardConfig = {}

    for (const key in ALL_METRICS) {
        const metric = ALL_METRICS[key]

        config[key] = {
            customLabel: '',
            customColor: '',
            order: metric.order,
            notificationConfiguration: {
                warningThreshold: 0,
                limitThreshold: 0,
                warningColor: '',
                limitColor: '',
                warningSound: '',
                limitSound: '',
                reverseThreshold: false
            }
        }
    }

    return config
}




